"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.joinKicadInstancePath = joinKicadInstancePath;
exports.kicadInstancePathWithoutRoot = kicadInstancePathWithoutRoot;
exports.parseProject = parseProject;
exports.normalizeDesignNetKeys = normalizeDesignNetKeys;
const NodeParser_1 = require("../common/NodeParser");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const board_1 = require("./board");
const ComponentResolver_1 = require("./resolvers/ComponentResolver");
const schematic_1 = require("./schematic");
const DesignModels_1 = require("@cadstrom/sigma-engine-shared/dist/DesignModels");
const Font_1 = require("../Font");
const RefableNet_1 = require("./RefableNet");
const ModelText_1 = require("../../ModelText");
const KicadHierarchyResolver_1 = require("./KicadHierarchyResolver");
function joinKicadInstancePath(instancePath, childSegment) {
    const segments = [...instancePath.split('/'), childSegment].filter((segment) => segment.length > 0);
    return `/${segments.join('/')}`;
}
function kicadInstancePathWithoutRoot(instancePath) {
    const segments = instancePath.split('/').filter((segment) => segment.length > 0);
    return `/${segments.slice(1).join('/')}`;
}
async function parseProject(def, opts = {}) {
    if (def.type !== 'kicad') {
        throw new Error(`Attempted to parse non-kicad project def: ${def.type}`);
    }
    if (typeof opts === 'boolean') {
        opts = { removeNetRefs: opts };
    }
    const parser = new NodeParser_1.SExpressionFileParser();
    const warnings = [];
    const parsedDef = await preParseProjectFiles(def, parser, warnings);
    const project = {
        type: def.type,
        modelVersion: ModelText_1.MODEL_TEXT_MIN_VERSION,
        layerSets: {},
        components: {},
        designs: {},
        warnings,
        modules: [],
        processingResults: {
            type: 'project-processing',
            message: 'Project processed successfully',
            innerResults: [],
        },
        fonts: {
            fonts: {},
        },
        globalNets: {},
    };
    if (def.name) {
        project.name = def.name;
    }
    const resolver = opts?.componentResolverFactory?.(project, parsedDef) ?? new ComponentResolver_1.KicadComponentResolver(project);
    const netRefCtx = new sigma_engine_shared_1.NetRefCtx();
    const parsedImages = [];
    for (const sheetInfo of parsedDef.kicadSchematics) {
        if (!sheetInfo.contents) {
            const schs = def.schematics[sheetInfo.filename];
            if (!schs) {
                project.warnings.push(`Could not find required input file: ${sheetInfo.filename}`);
                continue;
            }
            sheetInfo.contents = await schs.contents.text();
            sheetInfo.libContents = schs.lib;
        }
        const parsed = (0, schematic_1.parseSchematic)(parsedDef, parser, project, sheetInfo, resolver, netRefCtx, undefined, parsedImages);
        netRefCtx.clearOldRefs();
        project.designs[parsed.designId] = parsed;
    }
    for (const [brdId, brd] of Object.entries(def.boards)) {
        const parsed = (0, board_1.parseBoard)(project, await brd.contents.text(), brdId, resolver, netRefCtx);
        netRefCtx.clearOldRefs();
        project.designs[parsed.designId] = parsed;
    }
    registerImageAssets(project, parsedImages, opts.extractedImages);
    (0, KicadHierarchyResolver_1.resolveKicadHierarchyNets)(project);
    normalizeDesignNetKeys(project);
    (0, Font_1.packModelFonts)(project);
    if (opts.removeNetRefs) {
        return JSON.parse(JSON.stringify(project));
    }
    (0, DesignModels_1.computeAggragateMessageResultSeverity)(project.processingResults);
    return project;
}
function registerImageAssets(project, parsedImages, sink) {
    const byRef = new Map();
    for (const image of parsedImages) {
        if (!byRef.has(image.ref)) {
            byRef.set(image.ref, image);
        }
    }
    if (byRef.size === 0) {
        return;
    }
    project.assets = [...(project.assets ?? [])];
    for (const asset of byRef.values()) {
        project.assets.push({
            type: DesignModels_1.ProjectModelAssetType.Image,
            ref: asset.ref,
            name: asset.name,
            mimeType: asset.mimeType,
        });
    }
    sink?.push(...byRef.values());
}
function normalizeDesignNetKeys(project) {
    for (const design of Object.values(project.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        const rebuilt = {};
        const seen = new Set();
        for (const netInfo of (0, RefableNet_1.refableObjectValues)(design.nets)) {
            if (seen.has(netInfo)) {
                continue;
            }
            seen.add(netInfo);
            const name = String(netInfo.name);
            const existing = rebuilt[name];
            if (existing) {
                rebuilt[name] = {
                    ...existing,
                    ...netInfo,
                    name,
                    metadata: {
                        ...(existing.metadata ?? {}),
                        ...(netInfo.metadata ?? {}),
                    },
                    netLabels: (existing.netLabels ?? []).concat(netInfo.netLabels ?? []),
                    connections: existing.connections.concat(netInfo.connections.filter((c) => !existing.connections.some((e) => e.moduleRef === c.moduleRef && e.pinRef === c.pinRef))),
                };
            }
            else {
                rebuilt[name] = { ...netInfo, name };
            }
        }
        design.nets = rebuilt;
    }
}
async function preParseProjectFiles(def, parser, warnings) {
    const kicadSchematics = await (0, schematic_1.preParseSchematicSheets)(def, parser, warnings);
    return {
        ...def,
        kicadSchematics,
    };
}
//# sourceMappingURL=project.js.map